import { useNavigate } from 'react-router';
import { Users, Receipt, Banknote } from 'lucide-react';
import type { GroupWithMembers } from '@/api/groups';
import type { GroupStats } from '@/api/balances';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { UserAvatar } from '@/components/common/UserAvatar';
import { BudgetProgressBar } from '@/components/groups/BudgetProgressBar';
import type { UserBalance } from '@/hooks/useBalances';
import type { BudgetStatus } from '@/lib/budget';
import { useTranslation } from 'react-i18next';
import i18next from '@/i18n';

interface GroupCardProps {
  group: GroupWithMembers;
  userBalance?: UserBalance;
  groupStats?: GroupStats;
  isBalanceLoading?: boolean;
  disableNavigation?: boolean;
  budgetStatus?: BudgetStatus | null;
}

function getWeekLabel(weeksAgo: number): string {
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() - weeksAgo * 7);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);

  const fmt = (d: Date) =>
    d.toLocaleDateString(i18next.language, { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function ActivitySparkline({ data, color }: { data: number[]; color: string }) {
  const { t } = useTranslation();
  const max = Math.max(...data, 1);
  const weeks = data.length;
  return (
    <div className="flex items-end gap-0.5 h-6">
      {data.map((count, i) => {
        const weeksAgo = weeks - 1 - i;
        return (
          <div key={i} className="group/bar relative flex-1 min-w-[3px] self-stretch flex items-end">
            <div
              className="w-full rounded-sm transition-all group-hover/bar:opacity-80"
              style={{
                height: `${Math.max(2, (count / max) * 24)}px`,
                backgroundColor: count > 0 ? color : 'var(--color-surface-inset)',
                opacity: count > 0 ? 1 : 0.4,
              }}
            />
            {/* Tooltip */}
            <div className="pointer-events-none invisible group-hover/bar:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10">
              <div className="rounded-md bg-gray-900 dark:bg-zinc-100 px-2 py-1 text-[10px] text-white dark:text-zinc-900 whitespace-nowrap shadow-lg">
                <div className="font-medium">{t('web:groupCard.expenses', { count })}</div>
                <div className="text-gray-400 dark:text-zinc-500">{getWeekLabel(weeksAgo)}</div>
              </div>
              <div className="mx-auto h-0 w-0 border-x-[4px] border-x-transparent border-t-[4px] border-t-gray-900 dark:border-t-zinc-100" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="h-4 w-24 animate-pulse rounded bg-surface-inset" />
        <div className="h-4 w-20 animate-pulse rounded bg-surface-inset" />
      </div>
      <div className="flex items-center justify-between">
        <div className="h-4 w-28 animate-pulse rounded bg-surface-inset" />
        <div className="h-5 w-16 animate-pulse rounded bg-surface-inset" />
      </div>
    </div>
  );
}

export function GroupCard({ group, userBalance, groupStats, isBalanceLoading, disableNavigation, budgetStatus }: GroupCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const groupColor = group.color ?? '#6366F1';
  const hasExpenses = groupStats && groupStats.expenseCount > 0;
  const hasActivity = groupStats?.weeklyActivity.some(w => w > 0);

  return (
    <button
      onClick={disableNavigation ? undefined : () => navigate(`/groups/${group.id}`)}
      className={`flex w-full flex-col rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary text-left transition-all ${
        disableNavigation ? 'cursor-default' : 'hover:shadow-md hover:ring-border-secondary'
      }`}
    >
      {/* Header: color dot + name + description */}
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 h-4 w-4 rounded-full shrink-0"
          style={{ backgroundColor: groupColor }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary truncate">{group.name}</h3>
          </div>
          {group.description && (
            <p className="mt-0.5 text-sm text-text-tertiary truncate">{group.description}</p>
          )}
        </div>
      </div>

      {/* Stats section */}
      {isBalanceLoading ? (
        <StatsSkeleton />
      ) : (
        <div className="mt-3 space-y-1.5">
          {/* Row 1: expense count + balance */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm text-text-tertiary">
              <Receipt className="h-3.5 w-3.5" />
              <span>
                {groupStats
                  ? t('web:groupCard.expenses', { count: groupStats.expenseCount })
                  : '—'}
              </span>
            </div>
            {userBalance && userBalance.balanceMinor !== 0 ? (
              <AmountDisplay
                amountMinor={userBalance.balanceMinor}
                currency={group.currency_code}
                colorize
                className="text-sm font-medium"
              />
            ) : (
              <span className="text-sm text-text-quaternary">{t('groups:balances.settledUp')}</span>
            )}
          </div>

          {/* Row 2: total spent */}
          {hasExpenses && (
            <div className="flex items-center gap-1.5 text-sm text-text-tertiary">
              <Banknote className="h-3.5 w-3.5" />
              <AmountDisplay
                amountMinor={groupStats.totalSpentMinor}
                currency={group.currency_code}
                className="text-sm text-text-tertiary"
              />
              <span>total</span>
            </div>
          )}

          {!hasExpenses && groupStats && (
            <p className="text-xs text-text-quaternary">{t('expenses:empty.noExpensesYet')}</p>
          )}
        </div>
      )}

      {/* Budget progress (when enabled) */}
      {budgetStatus && (
        <div className="mt-3">
          <BudgetProgressBar status={budgetStatus} compact />
        </div>
      )}

      {/* Sparkline */}
      {hasActivity && groupStats && (
        <div className="mt-3">
          <ActivitySparkline data={groupStats.weeklyActivity} color={groupColor} />
          <p className="mt-0.5 text-[10px] text-text-quaternary">{t('web:groupCard.last8Weeks')}</p>
        </div>
      )}

      {/* Footer: member avatars */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex -space-x-2">
          {group.members.slice(0, 5).map(m => (
            <UserAvatar
              key={m.id}
              name={m.user?.display_name}
              avatarUrl={m.user?.avatar_url}
            />
          ))}
          {group.members.length > 5 && (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-tertiary text-xs font-medium text-text-tertiary ring-2 ring-surface-primary">
              +{group.members.length - 5}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-text-quaternary">
          <Users className="h-3 w-3" />
          <span>{group.members.length}</span>
        </div>
      </div>
    </button>
  );
}
